import { showErrorToast } from "../../../configurator/renderer/tools/helper";
import { useUiTranslator } from "../../../hooks/useUiTranslator";
import { EasySessionConversionError } from "../../../onboarding/EasySessionConversionError";
import { externalCredentialsHandoffAtom } from "../../../onboarding/externalCredentialsHandoff";
import {
  getGetApiEasyAdministrationAreaQueryKey,
  useGetApiQuoteApiGetQuoteId,
} from "../../../openapi/backendApi";
import { useEasySession } from "../../../providers/easySessionProvider";
import { useAtom } from "jotai";

import { JSX, useEffect, useEffectEvent, useRef, useState } from "react";

//import { useLanguage } from "@4pace-cpq/core";
import { Box, CircularProgress, Container } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";

interface ISearchObj {
  language: string;
  productCategory?: string;
}

export const CustomOpportunityRouteComponent = (): JSX.Element => {
  const t = useUiTranslator();
  const [isLoadingWegWebsite, setIsLoadingWegWebsite] = useState<boolean>(true);

  const { checkLogin } = useEasySession();
  const queryClient = useQueryClient();
  const [
    isExternalCredentialsHandoffInProgress,
    setExternalCredentialsHandoff,
  ] = useAtom(externalCredentialsHandoffAtom);
  const isHandlingHandoff = useRef(false);
  const [handoffError, setHandoffError] = useState<string | null>(null);
  const [isRetryingHandoff, setIsRetryingHandoff] = useState(false);
  const navigate = useNavigate();

  const {
    language,
    origin,
    purchaseCountry,
    catalog,
    customer,
    customerName,
    productCategory,
    intermediateToken,
    sessionFromEasy,
  } = useSearch({ strict: false });
  const {
    data: quoteId,
    isSuccess,
    refetch: refetchQuoteId,
  } = useGetApiQuoteApiGetQuoteId({
    query: { enabled: !isExternalCredentialsHandoffInProgress },
  });

  const handleNavigateToCart = ({
    productCategory,
    quoteIdOverride,
  }: {
    productCategory?: string;
    quoteIdOverride?: string;
  }): void => {
    const searchObj: ISearchObj = {
      language,
    };
    if (productCategory) {
      searchObj.productCategory = productCategory;
    }
    const resolvedQuoteId = quoteIdOverride ?? quoteId?.data;
    if (resolvedQuoteId) {
      navigate({
        to: `/cart/${resolvedQuoteId}`,
        search: () => searchObj,
        replace: true,
      });
    } else {
      console.error("Error - No valid quote exists. quoteId:", resolvedQuoteId);
    }
  };

  const getHandoffErrorMessage = async (
    response?: Response,
  ): Promise<string> => {
    const data = await response?.json().catch(() => undefined);
    return t(
      `CustomApp:${data?.key ?? "response.SomethingWentWrong"}`,
      "An error occurred",
      {
        // The backend sends `params: null` for e.g. response.NoQuoteFound,
        // whose translations interpolate {{params}} / {{sessionId}} - fall
        // back to the EASY session id so the message isn't cut off.
        sessionId: sessionFromEasy,
        params: data?.params ?? sessionFromEasy,
      },
    );
  };

  /** Sends the handoff request; resolves to an error message on failure. */
  const requestHandoff = async (
    request: () => Promise<Response>,
  ): Promise<string | undefined> => {
    try {
      const response = await request();
      return response.ok ? undefined : await getHandoffErrorMessage(response);
    } catch (error) {
      console.error("External credentials handoff failed", error);
      return getHandoffErrorMessage();
    }
  };

  /**
   * Runs a handoff request, then finishes onboarding with the session's quote.
   * On failure it either blocks with `handoffError` (the handoff stays pending,
   * so no anonymous quote is created and no onboarding dialogs open behind the
   * error) or toasts and continues with the session's anonymous quote.
   */
  const runHandoff = async ({
    request,
    navigateSearch,
    blockOnFailure,
  }: {
    request: () => Promise<Response>;
    navigateSearch: { productCategory?: string };
    blockOnFailure: boolean;
  }): Promise<void> => {
    if (isHandlingHandoff.current) return;
    isHandlingHandoff.current = true;
    setIsLoadingWegWebsite(true);
    setExternalCredentialsHandoff(true);

    const errorMessage = await requestHandoff(request);
    if (errorMessage !== undefined) {
      if (blockOnFailure) {
        setHandoffError(errorMessage);
        setIsLoadingWegWebsite(false);
        isHandlingHandoff.current = false;
        return;
      }
      showErrorToast(errorMessage);
    }

    try {
      await queryClient.invalidateQueries({
        queryKey: getGetApiEasyAdministrationAreaQueryKey(),
      });
      await checkLogin();
      const { data: refetchedQuoteId } = await refetchQuoteId();
      setHandoffError(null);
      setIsLoadingWegWebsite(false);
      handleNavigateToCart({
        ...navigateSearch,
        quoteIdOverride: refetchedQuoteId?.data,
      });
    } finally {
      setExternalCredentialsHandoff(false);
    }
  };

  const handlePostSessionFromWegWebsite = (): Promise<void> =>
    runHandoff({
      request: () =>
        fetch("/api/Easy/SessionFromWegWebsite", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            language,
            origin,
            purchaseCountry,
            catalog,
            customer,
            customerName,
            productCategory,
            intermediateToken,
          }),
        }),
      navigateSearch: { productCategory },
      blockOnFailure: false,
    });

  const handleUseSessionFromEasy = (): Promise<void> =>
    runHandoff({
      request: () =>
        fetch(`/api/QuoteApi/ConvertSessionFromEasy/${sessionFromEasy}`, {
          method: "GET",
        }),
      navigateSearch: {},
      // Landing in an anonymous cart is easy to miss - the user would lose
      // their EASY quote without noticing.
      blockOnFailure: true,
    });

  const handleRetrySessionFromEasy = async (): Promise<void> => {
    setIsRetryingHandoff(true);
    await handleUseSessionFromEasy();
    setIsRetryingHandoff(false);
  };

  const handleEffect = useEffectEvent((): void => {
    if (sessionFromEasy) {
      handleUseSessionFromEasy();
    } else if (origin && productCategory) {
      handlePostSessionFromWegWebsite();
    } else if (quoteId && !origin) {
      handleNavigateToCart({});
    }
  });

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    handleEffect();
  }, [isSuccess]);

  if (handoffError !== null) {
    return (
      <EasySessionConversionError
        detail={handoffError}
        isRetrying={isRetryingHandoff}
        onRetry={handleRetrySessionFromEasy}
      />
    );
  }

  return (
    <Container>
      <Box my={4}>
        {isLoadingWegWebsite || !quoteId ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            mb={4}
          >
            <CircularProgress />
          </Box>
        ) : (
          <></>
        )}
      </Box>
    </Container>
  );
};
